import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api, euro, formatApiError } from "../lib/api";
import {
    Clock,
    User,
    PencilSimple,
    Trash,
    Plus,
    LockKey,
    LockKeyOpen,
    ArrowsClockwise,
} from "@phosphor-icons/react";

const ACTION_META = {
    "transaction.create": { icon: Plus, label: "Nuova transazione", color: "#00C853" },
    "transaction.delete": { icon: Trash, label: "Elimina transazione", color: "#FF2A00" },
    "wallet.update": { icon: PencilSimple, label: "Modifica wallet", color: "#002FA7" },
    "provider.update": { icon: PencilSimple, label: "Modifica fornitore", color: "#002FA7" },
    "client.create": { icon: Plus, label: "Nuovo cliente", color: "#00C853" },
    "client.update": { icon: PencilSimple, label: "Modifica cliente", color: "#002FA7" },
    "client.delete": { icon: Trash, label: "Elimina cliente", color: "#FF2A00" },
    "closure.create": { icon: LockKey, label: "Chiusura mese", color: "#09090b" },
    "closure.delete": { icon: LockKeyOpen, label: "Riapertura mese", color: "#F59E0B" },
};

function timeAgo(isoString) {
    const diff = Date.now() - new Date(isoString).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "ora";
    if (m < 60) return `${m} min fa`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h fa`;
    const d = Math.floor(h / 24);
    return `${d}g fa`;
}

function fullDateTime(isoString) {
    return new Date(isoString).toLocaleString("it-IT", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export default function AuditLog() {
    const { agencyId } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterAction, setFilterAction] = useState("");

    const load = async () => {
        if (!agencyId) return;
        setLoading(true);
        try {
            const params = { agency_id: agencyId, limit: 200 };
            if (filterAction) params.action = filterAction;
            const r = await api.get("/audit-log", { params });
            setRows(r.data);
        } catch (e) {
            console.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agencyId, filterAction]);

    const actionTypes = Array.from(new Set(rows.map((r) => r.action)));

    return (
        <div data-testid="audit-log-page" className="space-y-6">
            <div>
                <div className="overline mb-2 flex items-center gap-2">
                    <Clock size={12} weight="bold" /> Cronologia azioni
                </div>
                <h1 className="text-3xl md:text-4xl font-black tracking-tighter">
                    Chi ha fatto cosa, ultime 72h.
                </h1>
                <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
                    Ogni azione su transazioni, wallet, fornitori, clienti e chiusure
                    viene tracciata e conservata per 72 ore. Solo lettura — usa questa
                    pagina per diagnosticare modifiche o capire chi ha registrato cosa.
                </p>
            </div>

            <div className="bg-white border border-zinc-200 p-4 flex items-center gap-3 flex-wrap">
                <div className="overline">Filtra per azione:</div>
                <button
                    onClick={() => setFilterAction("")}
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${filterAction === "" ? "bg-ink text-white border-ink" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`}
                    data-testid="audit-filter-all"
                >
                    Tutte
                </button>
                {actionTypes.map((a) => {
                    const meta = ACTION_META[a] || { label: a };
                    return (
                        <button
                            key={a}
                            onClick={() => setFilterAction(a)}
                            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${filterAction === a ? "bg-ink text-white border-ink" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`}
                            data-testid={`audit-filter-${a}`}
                        >
                            {meta.label}
                        </button>
                    );
                })}
                <button
                    onClick={load}
                    className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-zinc-600 hover:text-ink"
                    data-testid="audit-refresh"
                >
                    <ArrowsClockwise size={12} weight="bold" /> Aggiorna
                </button>
            </div>

            {loading ? (
                <div className="overline">Caricamento…</div>
            ) : rows.length === 0 ? (
                <div className="bg-white border border-zinc-200 p-8 text-center text-sm text-zinc-500">
                    Nessuna azione tracciata nelle ultime 72 ore.
                </div>
            ) : (
                <div className="bg-white border border-zinc-200">
                    {rows.map((r, idx) => {
                        const meta = ACTION_META[r.action] || { icon: Clock, label: r.action, color: "#71717a" };
                        const Icon = meta.icon;
                        return (
                            <div
                                key={`${r.at}-${r.action}-${r.entity_id || ""}`}
                                className="flex items-start gap-4 p-4 border-b border-zinc-100 last:border-0 hover:bg-zinc-50"
                                data-testid={`audit-row-${idx}`}
                            >
                                <div
                                    className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5"
                                    style={{
                                        background: `${meta.color}15`,
                                        color: meta.color,
                                    }}
                                >
                                    <Icon size={14} weight="bold" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-2 flex-wrap">
                                        <span
                                            className="text-[10px] font-bold uppercase tracking-wider"
                                            style={{ color: meta.color }}
                                        >
                                            {meta.label}
                                        </span>
                                        <span className="text-xs text-zinc-400">·</span>
                                        <span className="text-xs text-zinc-500 inline-flex items-center gap-1">
                                            <User size={10} weight="bold" />
                                            {r.operator_name || r.operator_id || "—"}
                                        </span>
                                    </div>
                                    <div className="text-sm text-zinc-800 mt-1 break-words">
                                        {r.description || "(nessuna descrizione)"}
                                    </div>
                                </div>
                                <div
                                    className="text-xs text-zinc-500 tabular-nums flex-shrink-0 text-right"
                                    title={fullDateTime(r.at)}
                                >
                                    <div className="font-bold text-zinc-700">{timeAgo(r.at)}</div>
                                    <div className="text-[10px] text-zinc-400 mt-0.5">
                                        {fullDateTime(r.at)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="text-xs text-zinc-400 text-center">
                I record vengono cancellati automaticamente dopo 72 ore.
            </div>
        </div>
    );
}
